import type React from 'react';
import { memo } from 'react';
import styled, { css } from 'styled-components';

import { BASE_FONT_SIZE } from '@wecasa/common/src/constants';
import type { FontWeight } from '@wecasa/common/src/types/components';
import colors from '@wecasa/common/src/utils/colors';
import { type ParsedText, parseText } from '@wecasa/common/src/utils/strings';
import { toRem } from '@wecasa/common/src/utils/styles';

import { TEXT_FONT } from '../../constants/styles';

// @deprecated
export const FONT_WEIGHTS = Object.freeze({
  REGULAR: 400,
  SEMI_BOLD: 500,
  BOLD: 700,
  BLACK: 900,
});

const defaultColor = colors.neutral100;
const defaultFontSize = BASE_FONT_SIZE;
const defaultLineHeight = 1.333;
const defaultFontWeight: FontWeight = 400;
const defaultLetterSpacing = 'normal';

const TextsContainer = styled.span<{
  $display?: string;
  $centered: boolean;
  $lineHeight?: number;
  $fontSize?: number;
}>`
  display: ${(props) => props.$display};
  text-align: ${(props) => (props.$centered ? 'center' : 'left')};
  line-height: ${(props) => props.$lineHeight || defaultLineHeight};
  font-size: ${(props) => toRem(props.$fontSize) || defaultFontSize};
`;

export type TextStyleProps = {
  $display?: string;
  $fontFamily?: string;
  $fontSize?: number;
  $fontWeight?: FontWeight;
  $lineHeight?: number;
  $centered?: boolean;
  $color?: string;
  $letterSpacing?: string;
  $strikedOut?: boolean;
  $isSup?: boolean;
  $isItalic?: boolean;
};

export const textStyle = css<TextStyleProps>`
  font-family: ${(props) => props.$fontFamily || TEXT_FONT};
  ${(props) => `
    font-size: ${toRem(props.$fontSize || defaultFontSize)};
    font-weight: ${props.$fontWeight || defaultFontWeight};
    line-height: ${props.$lineHeight || defaultLineHeight};
    text-align: ${props.$centered ? 'center' : 'left'};
    color: ${props.$color || defaultColor};
    letter-spacing: ${props.$letterSpacing || defaultLetterSpacing};
    text-decoration-line: ${props.$strikedOut ? 'line-through' : 'none'};
  ${
    props.$isSup &&
    `
      vertical-align: super;
      font-size: ${toRem((props.$fontSize || defaultFontSize) * 0.7)};
      line-height: 0;
    `
  };
    font-style: ${props.$isItalic ? 'italic' : 'normal'};
  `};
`;

const StyledText = styled.span<TextStyleProps>`
  display: ${(props) => props.$display};
  ${textStyle}
`;

/*
 * Style applied to emphasised (italic `__…__`) segments instead of a plain `font-style: italic`.
 * BaseText stays domain-agnostic — the caller decides what emphasis means. `Title` passes its
 * level's Tertiary spec (Instrument Serif at the Tertiary optical size). Carries its own
 * `lineHeight` — taller than the base variant's and matched web↔mobile — so the tall Instrument
 * Serif glyphs are not clipped (React Native clips a glyph exceeding its line box; the web doesn't).
 */
export type EmphasisStyle = {
  fontFamily?: string;
  fontSize: number;
  fontWeight: FontWeight;
  lineHeight: number;
};

export type TextProps = {
  id?: string;
  display?: 'block' | 'inline' | 'inline-block' | 'flex';
  color?: string;
  fontFamily?: string;
  fontSize?: number;
  lineHeight?: number;
  fontWeight?: FontWeight;
  centered?: boolean;
  className?: string;
  htmlTag?: React.ElementType;
  children: string | number | React.ReactNode;
  letterSpacing?: string;
  strikedOut?: boolean;
  italic?: boolean;
  emphasisStyle?: EmphasisStyle;
};

const BaseText = ({
  id,
  children = '',
  color = defaultColor,
  fontFamily,
  fontSize = defaultFontSize,
  fontWeight = defaultFontWeight,
  lineHeight = defaultLineHeight,
  centered = false,
  className,
  htmlTag,
  display,
  letterSpacing,
  strikedOut = false,
  italic = false,
  emphasisStyle,
}: TextProps) => {
  const textChildren = children ?? ''; // if children is `null`, defaultProps ('') will not be used.
  const parsedText = parseText(String(textChildren));

  if (!parsedText.length) return null;
  // In case we want to render children that are different from string or number
  // we don't want to parse them, we want to render them as they are but still stay consistent in our style
  if (!['string', 'number'].includes(typeof children)) {
    return (
      <StyledText
        $fontFamily={fontFamily}
        $fontWeight={fontWeight}
        $color={color}
        $fontSize={fontSize}
        $lineHeight={lineHeight}
        $letterSpacing={letterSpacing}
        $strikedOut={strikedOut}
        $isItalic={italic}
        className={className}
        as={htmlTag}
      >
        {children}
      </StyledText>
    );
  }
  /*
   * We set a div container if parsing returns multiple texts so that they are
   * rendered side by side, like a standard sentence. Without it, each text would
   * be rendered as a unique child of the parent component, and in this case each of
   * them would inherit its display property, it could lead to unwanted / broken layouts.
   */
  /*
   * An italic (`__…__`) segment renders with `emphasisStyle` (Title uses this for its Tertiary
   * variant); every other segment keeps the base props. Shared by both render paths below.
   */
  const emphasisProps = (segment: ParsedText) => {
    const hasEmphasis = Boolean(segment.italic && emphasisStyle);

    return {
      $fontFamily: hasEmphasis ? emphasisStyle?.fontFamily : fontFamily,
      $fontWeight: hasEmphasis ? emphasisStyle?.fontWeight : segment.bold ? 700 : fontWeight,
      $fontSize: hasEmphasis ? emphasisStyle?.fontSize : fontSize,
      $lineHeight: hasEmphasis ? emphasisStyle?.lineHeight : lineHeight,
    };
  };

  return parsedText.length > 1 ? (
    <TextsContainer
      id={id}
      as={htmlTag}
      // className carries whole-title styles (margin, centering, …) → belongs on the block
      // container, not on each inline segment (else a split title loses its spacing/centering).
      className={className}
      $display={display || 'block'}
      $centered={centered}
      $fontSize={fontSize}
      $lineHeight={lineHeight}
    >
      {parsedText.map((text, index) => (
        <StyledText
          // biome-ignore lint/suspicious/noArrayIndexKey: reason
          key={index}
          {...emphasisProps(text)}
          $color={color}
          $letterSpacing={letterSpacing}
          $strikedOut={strikedOut}
          $isSup={text.sup}
          $isItalic={italic || text.italic}
        >
          {text.text}
        </StyledText>
      ))}
    </TextsContainer>
  ) : (
    <StyledText
      id={id}
      {...emphasisProps(parsedText[0])}
      $color={color}
      $centered={centered}
      $strikedOut={strikedOut}
      $isItalic={italic || parsedText[0].italic}
      className={className}
      as={htmlTag}
      $display={display}
    >
      {parsedText[0].text}
    </StyledText>
  );
};

export default memo(BaseText);
